import { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, deleteField, doc, getDocs, onSnapshot, setDoc, updateDoc, writeBatch } from '../fs';
import { db } from '../firebase';
import { usePanel } from '../App.jsx';
import { CLINICAS, nombreClinica, clinicaPorTexto } from '../clinicas';
import { eur, hoyISO, num, tel, useColeccion } from '../dia/comun';
import { validarImporte, validarTexto, validarTelefono, validarFecha, primerError } from '../validar.js';

// Igualas (planes de salud): quién la tiene, desde cuándo, qué cuotas se han cobrado y quién debe.
// Cuotas por periodo: mensual → 'AAAA-MM'; anual → 'AAAA' (año de cada aniversario del alta).

const METODOS = { domiciliacion: 'Domiciliación', tarjeta: 'Tarjeta', efectivo: 'Efectivo', transferencia: 'Transferencia', bizum: 'Bizum' };
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const mesTxt = (p) => (p?.length === 7 ? `${MESES[+p.slice(5) - 1]} ${p.slice(2, 4)}` : p || '');
const mesActual = () => hoyISO().slice(0, 7);
const sumaMes = (p, n) => { const d = new Date(Date.UTC(+p.slice(0, 4), +p.slice(5) - 1 + n, 1)); return d.toISOString().slice(0, 7); };
const fecha = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// Periodos que tocan desde el alta hasta hoy (o hasta la baja)
export function periodos(ig, hoy = hoyISO()) {
  if (!ig.alta) return [];
  const fin = ig.baja && ig.baja < hoy ? ig.baja : hoy;
  const out = [];
  if (ig.periodicidad === 'anual') {
    for (let y = +ig.alta.slice(0, 4); `${y}${ig.alta.slice(4)}` <= fin && out.length < 50; y++) out.push(String(y));
  } else {
    for (let p = ig.alta.slice(0, 7); p <= fin.slice(0, 7) && out.length < 240; p = sumaMes(p, 1)) out.push(p);
  }
  return out;
}
const pagado = (ig, p) => !!ig.pagos?.[p] || (ig.pagadoHasta && p <= ig.pagadoHasta);

// Estado de una iguala: al día, pendiente este periodo, debe N cuotas, baja
export function estadoIguala(ig, hoy = hoyISO()) {
  const ps = periodos(ig, hoy);
  const sinPagar = ps.filter((p) => !pagado(ig, p));
  const actual = ig.periodicidad === 'anual' ? ps[ps.length - 1] : hoy.slice(0, 7);
  // La cuota de este periodo cuenta como deuda si está marcada para revisar (p. ej. recibo devuelto)
  const atrasadas = sinPagar.filter((p) => p !== actual || ig.revisar);
  const meses = Math.max(0, Math.round((new Date(`${(ig.baja && ig.baja < hoy ? ig.baja : hoy)}T12:00:00`) - new Date(`${ig.alta}T12:00:00`)) / (30.44 * 864e5)));
  return {
    ps, sinPagar, atrasadas, actual,
    debe: atrasadas.length * (ig.cuota || 0),
    pendienteActual: !!actual && sinPagar.includes(actual) && !(ig.baja && ig.baja < hoy),
    baja: !!(ig.baja && ig.baja <= hoy),
    impago: !(ig.baja && ig.baja <= hoy) && (atrasadas.length > 0 || !!ig.revisar),
    meses,
    futura: !!ig.alta && ig.alta > hoy, // AUD-IG-05: aún no ha empezado
  };
}
const antig = (m) => (m < 1 ? 'este mes' : m < 12 ? `${m} ${m === 1 ? 'mes' : 'meses'}` : `${Math.floor(m / 12)} a${Math.floor(m / 12) === 1 ? 'ño' : 'ños'}${m % 12 ? ` y ${m % 12} m` : ''}`);

function usePlanes() {
  const [planes, setPlanes] = useState([]);
  useEffect(() => onSnapshot(doc(db, 'config', 'igualas'), (s) => setPlanes(s.data()?.planes || []), () => {}), []);
  return planes;
}

// Carga inicial (una sola vez): las igualas del Excel de octubre 2026, cada una en su clínica
function useCargaInicial() {
  // (La carga inicial desde Excel se hace ahora con «Importar Excel»; aquí ya no se mete ningún dato fijo)
  return '';
}

export default function Igualas() {
  const { user, esAdmin, esEquipo, clinica, clinicasActivas } = usePanel();
  const clinicasIg = clinicasActivas;
  const lista = useColeccion('igualas', clinicasIg);
  const planes = usePlanes();
  const carga = useCargaInicial(esAdmin);
  const [tab, setTab] = useState('lista');
  const [filtro, setFiltro] = useState('activas');
  const [q, setQ] = useState('');
  const [abierta, setAbierta] = useState(null);
  const [nueva, setNueva] = useState(false);
  const yo = user?.displayName || user?.email || '';
  const hoy = hoyISO();

  const conEstado = useMemo(() => (lista || []).map((ig) => ({ ...ig, e: estadoIguala(ig, hoy) })), [lista, hoy]);
  const cuenta = {
    activas: conEstado.filter((x) => !x.e.baja).length,
    impagos: conEstado.filter((x) => x.e.impago).length,
    pendientes: conEstado.filter((x) => !x.e.baja && x.e.pendienteActual && !x.e.impago).length,
    bajas: conEstado.filter((x) => x.e.baja).length,
  };
  const visibles = conEstado
    .filter((x) => (filtro === 'activas' ? !x.e.baja : filtro === 'impagos' ? x.e.impago : filtro === 'pendientes' ? !x.e.baja && x.e.pendienteActual && !x.e.impago : filtro === 'bajas' ? x.e.baja : true))
    .filter((x) => !q || `${x.mascota} ${x.cliente} ${x.titular || ''} ${x.telefono} ${x.plan}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(b.e.impago) - Number(a.e.impago) || b.e.atrasadas.length - a.e.atrasadas.length || (a.alta || '').localeCompare(b.alta || ''));

  return (
    <section>
      <div className="cabecera"><div><h1>Igualas</h1><p className="subtitulo">{nombreClinica(clinica)} · planes de salud, cobros e impagos</p></div>
        <div className="acciones">
          <button className="btn" onClick={() => { setNueva(true); setTab('lista'); }}>+ Nueva iguala</button>
        </div>
      </div>
      {carga && <div className="aviso-cita" style={{ marginBottom: '.8rem' }}>✓ {carga}</div>}
      {esAdmin && !esEquipo && <Resumen l={conEstado} />}
      <div className="filtros" style={{ marginBottom: '.8rem', flexWrap: 'wrap' }}>
        {[['lista', 'Igualas'], ['cobro', 'Cobro del mes'], ['planes', 'Planes'], ['importar', 'Importar']].filter(([k]) => esAdmin || k === 'lista' || k === 'cobro').map(([k, t]) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{t}{k === 'lista' && cuenta.impagos > 0 && <span className="pend-badge">{cuenta.impagos}</span>}</button>
        ))}
      </div>

      {tab === 'lista' && <>
        {nueva && <FormIguala planes={planes} clinicas={clinicasIg} onCerrar={() => setNueva(false)} onGuardar={async (d) => { await addDoc(collection(db, 'igualas'), { ...d, creado: new Date().toISOString(), creadoPor: yo }); setNueva(false); }} />}
        <div className="ig-barra">
          <div className="filtros">
            {[['activas', 'Activas', cuenta.activas], ['impagos', 'Revisar / impagos', cuenta.impagos], ['pendientes', 'Sin cobrar este mes', cuenta.pendientes], ['bajas', 'Bajas', cuenta.bajas], ['todas', 'Todas', conEstado.length]].map(([k, t, n]) => (
              <button key={k} className={filtro === k ? 'on' : ''} onClick={() => setFiltro(k)}>{t} <span className="tenue">{n}</span></button>
            ))}
          </div>
          <input className="campo" placeholder="Buscar mascota, dueño, titular o teléfono…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} />
        </div>
        <div className="tarjeta" style={{ padding: 0 }}>
          {!lista ? <p className="tenue" style={{ padding: '1rem' }}>Cargando…</p> : !visibles.length ? <p className="tenue" style={{ padding: '1rem' }}>{conEstado.length ? 'Nada con este filtro.' : 'Aún no hay igualas. Añádelas con "+ Nueva iguala" o impórtalas desde Excel.'}</p>
            : visibles.map((ig) => <FilaIguala key={ig.id} ig={ig} abierta={abierta === ig.id} onAbrir={() => setAbierta(abierta === ig.id ? null : ig.id)} planes={planes} clinicas={clinicasIg} yo={yo} varias={clinicasIg.length > 1} esAdmin={esAdmin} />)}
        </div>
      </>}
      {tab === 'cobro' && <CobroMes l={conEstado} yo={yo} varias={clinicasIg.length > 1} />}
      {tab === 'planes' && esAdmin && <Planes planes={planes} />}
      {tab === 'importar' && esAdmin && <Importar planes={planes} clinicas={clinicasIg} yo={yo} existentes={lista || []} />}
    </section>
  );
}

function Resumen({ l }) {
  const act = l.filter((x) => !x.e.baja);
  const mrr = act.reduce((a, x) => a + (x.periodicidad === 'anual' ? (x.cuota || 0) / 12 : x.cuota || 0), 0);
  const imp = l.filter((x) => x.e.impago);
  const mes = mesActual();
  const altas = l.filter((x) => x.alta?.startsWith(mes)).length;
  const bajas = l.filter((x) => x.baja?.startsWith(mes)).length;
  const porClin = CLINICAS.map((c) => [c, act.filter((x) => x.clinica === c.id).length]).filter(([, n]) => n);
  return (
    <div className="ig-resumen">
      <div><small>Activas</small><b>{act.length}</b><span>{porClin.map(([c, n]) => `${c.nombre} ${n}`).join(' · ')}</span></div>
      <div><small>Ingreso recurrente</small><b>{eur(mrr)}</b><span>al mes · {eur(mrr * 12)} al año</span></div>
      <div className={imp.length ? 'mal' : ''}><small>Impagos / revisar</small><b>{imp.length}</b><span>{eur(imp.reduce((a, x) => a + x.e.debe, 0))} pendiente</span></div>
      <div><small>Este mes</small><b>+{altas} / −{bajas}</b><span>altas / bajas</span></div>
    </div>
  );
}

function FilaIguala({ ig, abierta, onAbrir, planes, clinicas, yo, varias, esAdmin }) {
  const e = ig.e;
  const [editar, setEditar] = useState(false);
  const [baja, setBaja] = useState(null);
  const ref = doc(db, 'igualas', ig.id);
  const cobrar = (p) => updateDoc(ref, { [`pagos.${p}`]: { importe: ig.cuota || 0, fecha: new Date().toISOString(), por: yo } });
  const quitarPago = (p) => updateDoc(ref, { [`pagos.${p}`]: deleteField() });
  const clin = CLINICAS.find((c) => c.id === ig.clinica)?.nombre || '';
  const msgImpago = () => {
    const ms = e.atrasadas.map((p) => (p.length === 4 ? `la cuota anual de ${p}` : mesTxt(p).replace(' ', ' 20'))).join(', ');
    const t = `Buenos días${ig.cliente ? `, ${ig.cliente.split(' ')[0]}` : ''}. Le escribimos de ${clin}. Revisando el plan de salud${ig.mascota ? ` de ${ig.mascota}` : ''}, nos consta pendiente ${e.atrasadas.length === 1 ? 'la cuota de' : 'las cuotas de'} ${ms}${e.debe ? ` (${eur(e.debe)})` : ''}. ¿Podría pasarse por la clínica o indicarnos cómo prefiere abonarla? Si ya lo ha hecho, disculpe las molestias. Muchas gracias.`;
    window.open(`https://wa.me/${tel(ig.telefono)}?text=${encodeURIComponent(t)}`, '_blank');
    updateDoc(ref, { avisos: [...(ig.avisos || []), { fecha: new Date().toISOString(), por: yo, tipo: 'whatsapp' }] });
  };
  const ultimos = e.ps.slice(-12);
  return (
    <div className={`ig-fila ${e.impago ? 'impago' : ''} ${e.baja ? 'baja' : ''}`}>
      <div className="ig-cab" onClick={onAbrir}>
        <div className="ig-nombre">
          <b>{ig.cliente || 'Sin dueño'}</b>
          <div className="ig-masc">{ig.mascota ? <span>🐾 {ig.mascota}</span> : <span className="falta">🐾 Falta la mascota</span>}{ig.titular && ig.titular.trim().toLowerCase() !== (ig.cliente || '').trim().toLowerCase() && <span className="ig-tit">Recibo a nombre de <b>{ig.titular}</b></span>}</div>
          <div className="tenue" style={{ fontSize: '.78rem' }}>{ig.plan || 'Plan'}{ig.cuota ? ` · ${eur(ig.cuota)}/${ig.periodicidad === 'anual' ? 'año' : 'mes'}` : ''} · {METODOS[ig.metodo] || 'sin método'}{varias ? ` · ${clin}` : ''}</div>
        </div>
        <div className="ig-desde"><small>Desde</small><b>{fecha(ig.alta)}</b><span className="tenue">{antig(e.meses)}</span></div>
        <div className="ig-estado">
          {e.futura ? <span className="chip">Empieza el {fecha(ig.alta)}</span>
            : e.baja ? <span className="chip">Baja {fecha(ig.baja)}</span>
            : e.atrasadas.length ? <span className="chip mal">Debe {e.atrasadas.length} {e.atrasadas.length === 1 ? 'cuota' : 'cuotas'}</span>
              : ig.revisar ? <span className="chip aviso">Revisar</span>
                : e.pendienteActual ? <span className="chip aviso">Sin cobrar {ig.periodicidad === 'anual' ? e.actual : mesTxt(e.actual)}</span>
                  : <span className="chip bien">Al día</span>}
        </div>
        <span className="ig-flecha">{abierta ? '▾' : '▸'}</span>
      </div>
      {abierta && (
        <div className="ig-detalle">
          {ig.revisar && <div className="aviso-cita" style={{ marginBottom: '.5rem' }}>⚠️ Para revisar{ig.notaRevision ? `: ${ig.notaRevision}` : ''}</div>}
          <div className="ig-meses">
            {ultimos.map((p) => {
              const ok = pagado(ig, p);
              return (
                <button key={p} title={ok ? (ig.pagos?.[p] ? `Cobrado ${new Date(ig.pagos[p].fecha).toLocaleDateString('es-ES')} · ${ig.pagos[p].por || ''}` : 'Pagado (importado)') : 'Marcar como cobrado'}
                  className={ok ? 'ok' : p === e.actual ? 'ya' : 'debe'}
                  onClick={() => (ok ? ig.pagos?.[p] && window.confirm(`¿Quitar el cobro de ${mesTxt(p)}? (p. ej. recibo devuelto)`) && quitarPago(p) : cobrar(p))}>
                  <small>{p.length === 4 ? p : mesTxt(p)}</small>{ok ? '✓' : p === e.actual ? '·' : '✕'}
                </button>
              );
            })}
          </div>
          <p className="tenue" style={{ fontSize: '.75rem', margin: '.2rem 0 .5rem' }}>Toca un mes para marcarlo cobrado; tócalo otra vez si el recibo se devuelve.{e.ps.length > 12 ? ` (se muestran los últimos 12 de ${e.ps.length})` : ''}</p>
          <div className="ig-editar-rapido">
            <label><span>Mascota(s)</span><input className="campo" defaultValue={ig.mascota || ''} key={`m${ig.mascota}`} placeholder="Toby, Luna…"
              onBlur={(e) => e.target.value.trim() !== (ig.mascota || '') && updateDoc(ref, { mascota: e.target.value.trim() })} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} /></label>
            <label><span>Titular del recibo {ig.metodo === 'domiciliacion' ? '(cuenta del banco)' : ''}</span><input className="campo" defaultValue={ig.titular || ''} key={`t${ig.titular}`} placeholder={`Igual que el dueño (${ig.cliente || '—'})`}
              onBlur={(e) => e.target.value.trim() !== (ig.titular || '') && updateDoc(ref, { titular: e.target.value.trim() })} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} /></label>
          </div>
          <div className="ig-datos">
            {ig.telefono && <span>📞 <a href={`tel:${ig.telefono}`}>{ig.telefono}</a></span>}
            {ig.especie && <span>{ig.especie}</span>}
            {ig.notas && <span>📝 {ig.notas}</span>}
            {ig.avisos?.length > 0 && <span className="tenue">Avisado por WhatsApp {ig.avisos.length} {ig.avisos.length === 1 ? 'vez' : 'veces'} (última {new Date(ig.avisos[ig.avisos.length - 1].fecha).toLocaleDateString('es-ES')})</span>}
            {ig.baja && ig.motivoBaja && <span>Motivo de baja: {ig.motivoBaja}</span>}
          </div>
          <div className="acciones" style={{ flexWrap: 'wrap', marginTop: '.5rem' }}>
            {!e.baja && e.atrasadas.length > 0 && ig.telefono && <button className="btn mini" onClick={msgImpago}>📲 Recordar el pago por WhatsApp</button>}
            {!e.baja && <button className="btn sec mini" onClick={() => {
              if (ig.revisar) return updateDoc(ref, { revisar: false, notaRevision: deleteField() });
              const n = window.prompt('¿Qué hay que revisar? (recibo devuelto, cuenta mal, no contesta…)', '');
              if (n !== null) updateDoc(ref, { revisar: true, notaRevision: n });
            }}>{ig.revisar ? '✓ Ya revisado' : '⚠️ Marcar para revisar'}</button>}
            <button className="btn sec mini" onClick={() => setEditar(!editar)}>✎ Editar</button>
            {!e.baja ? <button className="btn sec mini" onClick={() => setBaja({ fecha: hoyISO(), motivo: '' })}>Dar de baja</button>
              : <button className="btn sec mini" onClick={() => updateDoc(ref, { baja: null, motivoBaja: deleteField() })}>Reactivar</button>}
            {esAdmin && <button className="hosp-x" title="Borrar" onClick={() => window.confirm(`¿Borrar la iguala de ${ig.mascota || ig.cliente}? (si se ha ido, mejor "Dar de baja")`) && deleteDoc(ref)}>✕</button>}
          </div>
          {baja && (
            <div className="hosp-nuevo">
              <label className="tenue">Fecha de baja <input type="date" className="campo" min={ig.alta} value={baja.fecha} onChange={(ev) => setBaja({ ...baja, fecha: ev.target.value })} /></label>
              <input className="campo" placeholder="Motivo (fallecido, se muda, no paga…)" maxLength={200} value={baja.motivo} onChange={(ev) => setBaja({ ...baja, motivo: ev.target.value })} />
              <button className="btn mini" disabled={!validarFecha(baja.fecha).ok || (ig.alta && baja.fecha < ig.alta)} onClick={() => { updateDoc(ref, { baja: baja.fecha, motivoBaja: baja.motivo.trim().slice(0, 200), revisar: false }); setBaja(null); }}>Confirmar baja</button>
              {ig.alta && baja.fecha < ig.alta && <small className="error">La baja no puede ser anterior al alta</small>}
              <button className="btn sec mini" onClick={() => setBaja(null)}>Cancelar</button>
            </div>
          )}
          {editar && <FormIguala inicial={ig} planes={planes} clinicas={clinicas} onCerrar={() => setEditar(false)} onGuardar={async (d) => { await updateDoc(ref, d); setEditar(false); }} />}
        </div>
      )}
    </div>
  );
}

function FormIguala({ inicial, planes, clinicas, onGuardar, onCerrar }) {
  const [f, setF] = useState({
    clinica: inicial?.clinica || clinicas[0], mascota: inicial?.mascota || '', especie: inicial?.especie || 'perro', cliente: inicial?.cliente || '', titular: inicial?.titular || '', telefono: inicial?.telefono || '',
    plan: inicial?.plan || '', cuota: inicial?.cuota != null ? String(inicial.cuota) : '', periodicidad: inicial?.periodicidad || 'mensual', metodo: inicial?.metodo || 'domiciliacion',
    alta: inicial?.alta || hoyISO(), pagadoHasta: inicial?.pagadoHasta || '', notas: inicial?.notas || '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const delaClinica = planes.filter((p) => !p.clinica || p.clinica === f.clinica);
  const elegirPlan = (nombre) => {
    const p = delaClinica.find((x) => x.nombre === nombre);
    setF({ ...f, plan: nombre, ...(p ? { cuota: String(p.cuota ?? ''), periodicidad: p.periodicidad || 'mensual' } : {}) });
  };
  // AUD-IG-01/02/05: cuota no negativa y acotada, teléfono válido, alta no futura (máx. 31 días), textos acotados
  const limiteAlta = hoyISO(new Date(Date.now() + 31 * 864e5));
  const vCuota = validarImporte(f.cuota, { etiqueta: 'La cuota', max: 5000 });
  const err = primerError(
    validarTexto(f.mascota, { etiqueta: 'La mascota', max: 120 }), validarTexto(f.cliente, { etiqueta: 'El dueño', max: 120 }),
    validarTexto(f.titular, { etiqueta: 'El titular', max: 120 }), validarTelefono(f.telefono), vCuota,
    validarFecha(f.alta, { etiqueta: 'La fecha de alta', hoy: limiteAlta, noFutura: true }), validarTexto(f.notas, { etiqueta: 'Las notas', max: 500 }),
    f.pagadoHasta && f.periodicidad === 'anual' && !/^\d{4}$/.test(f.pagadoHasta) ? { ok: false, error: 'Cobrado hasta: pon un año (ej. 2026)' } : null,
  );
  const ok = (f.mascota.trim() || f.cliente.trim()) && f.alta && !err;
  return (
    <form className="tarjeta ig-form" onSubmit={(e) => { e.preventDefault(); if (!ok) return; onGuardar({ ...f, mascota: f.mascota.trim(), cliente: f.cliente.trim(), titular: f.titular.trim(), telefono: f.telefono.trim(), notas: f.notas.trim(), cuota: vCuota.valor ?? 0, pagadoHasta: f.pagadoHasta || null }); }}>
      <h3 style={{ margin: '0 0 .6rem' }}>{inicial ? 'Editar iguala' : 'Nueva iguala'}</h3>
      <div className="ig-campos">
        {clinicas.length > 1 && <label><span>Clínica</span><select value={f.clinica} onChange={set('clinica')}>{CLINICAS.filter((c) => clinicas.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></label>}
        <label><span>Mascota(s)</span><input className="campo" value={f.mascota} onChange={set('mascota')} autoFocus={!inicial} placeholder="Toby, Luna…" /></label>
        <label><span>Especie</span><select value={f.especie} onChange={set('especie')}><option value="perro">Perro</option><option value="gato">Gato</option><option value="otro">Otro</option></select></label>
        <label><span>Dueño *</span><input className="campo" value={f.cliente} onChange={set('cliente')} /></label>
        <label><span>Titular del recibo</span><input className="campo" value={f.titular} onChange={set('titular')} placeholder="Si el banco lo cobra a otro nombre" /></label>
        <label><span>Teléfono</span><input className="campo" value={f.telefono} onChange={set('telefono')} inputMode="tel" /></label>
        <label><span>Plan</span><input className="campo" list="ig-planes" value={f.plan} onChange={(e) => elegirPlan(e.target.value)} placeholder="Plan cachorro, adulto…" />
          <datalist id="ig-planes">{delaClinica.map((p) => <option key={p.nombre} value={p.nombre} />)}</datalist></label>
        <label><span>Cuota (€)</span><input className="campo" min="0" value={f.cuota} onChange={set('cuota')} inputMode="decimal" /></label>
        <label><span>Se paga</span><select value={f.periodicidad} onChange={set('periodicidad')}><option value="mensual">Cada mes</option><option value="anual">Una vez al año</option></select></label>
        <label><span>Cómo paga</span><select value={f.metodo} onChange={set('metodo')}>{Object.entries(METODOS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select></label>
        <label><span>Activa desde *</span><input type="date" className="campo" value={f.alta} max={limiteAlta} onChange={set('alta')} /></label>
        <label title="Si la iguala ya existía: hasta qué mes está todo cobrado. Lo anterior cuenta como pagado."><span>Cobrado hasta {f.periodicidad === 'anual' ? '(año)' : '(mes)'}</span>
          {f.periodicidad === 'anual' ? <input className="campo" value={f.pagadoHasta} onChange={set('pagadoHasta')} placeholder="2026" inputMode="numeric" />
            : <input type="month" className="campo" value={f.pagadoHasta} onChange={set('pagadoHasta')} />}</label>
        <label className="ancho"><span>Notas</span><input className="campo" maxLength={500} value={f.notas} onChange={set('notas')} placeholder="IBAN cambiado, descuento, etc." /></label>
      </div>
      {err && <p className="error" role="alert" style={{ margin: '.5rem 0 0' }}>{err}</p>}
      <div className="acciones" style={{ marginTop: '.6rem' }}>
        <button className="btn" disabled={!ok}>Guardar</button>
        <button type="button" className="btn sec" onClick={onCerrar}>Cancelar</button>
      </div>
    </form>
  );
}

// Cobro del mes: marcar de una vez todas las domiciliadas cobradas y ver lo que queda
function CobroMes({ l, yo, varias }) {
  const [mes, setMes] = useState(mesActual());
  const act = l.filter((x) => !x.e.baja && x.periodicidad !== 'anual' && x.alta && x.alta.slice(0, 7) <= mes && !(x.baja && x.baja.slice(0, 7) < mes));
  const anuales = l.filter((x) => !x.e.baja && x.periodicidad === 'anual' && x.alta?.slice(5, 7) === mes.slice(5) && x.alta.slice(0, 7) <= mes);
  const pend = act.filter((x) => !pagado(x, mes));
  const [marcadas, setMarcadas] = useState(null);
  const sel = marcadas ?? new Set(pend.filter((x) => x.metodo === 'domiciliacion' && !x.revisar).map((x) => x.id));
  const toggle = (id) => { const s = new Set(sel); if (s.has(id)) s.delete(id); else s.add(id); setMarcadas(s); };
  const [hecho, setHecho] = useState('');
  const cobrar = async () => {
    const ids = [...sel];
    await Promise.all(ids.map((id) => { const x = l.find((y) => y.id === id); return updateDoc(doc(db, 'igualas', id), { [`pagos.${mes}`]: { importe: x?.cuota || 0, fecha: new Date().toISOString(), por: yo } }); }));
    setMarcadas(new Set()); setHecho(`${ids.length} cuotas marcadas como cobradas.`);
  };
  return (
    <div className="tarjeta">
      <div className="acciones" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <label className="tenue">Mes <input type="month" className="campo" value={mes} onChange={(e) => { setMes(e.target.value); setMarcadas(null); setHecho(''); }} /></label>
        <span className="tenue">{act.length - pend.length} de {act.length} mensuales cobradas</span>
      </div>
      <p className="tenue" style={{ fontSize: '.85rem' }}>Cuando pase la remesa del banco, deja marcadas las que han entrado (por defecto, todas las domiciliadas que no están para revisar) y dale a cobrar. Las devueltas, márcalas luego "para revisar".</p>
      {!pend.length ? <p>✓ Todo cobrado en {mesTxt(mes)}.</p> : (
        <>
          <div className="ig-cobro">
            {pend.map((x) => (
              <label key={x.id} className={x.revisar || x.e.atrasadas.length ? 'mal' : ''}>
                <input type="checkbox" checked={sel.has(x.id)} onChange={() => toggle(x.id)} />
                <b>{x.cliente || x.mascota}</b> <span className="tenue">{x.mascota ? `🐾 ${x.mascota}` : ''}{x.titular ? ` · recibo: ${x.titular}` : ''}{varias ? ` · ${nombreClinica(x.clinica)}` : ''}</span>
                <span className="tenue" style={{ marginLeft: 'auto' }}>{METODOS[x.metodo] || ''} · {eur(x.cuota)}{x.revisar ? ' · ⚠️' : ''}</span>
              </label>
            ))}
          </div>
          <div className="acciones" style={{ marginTop: '.6rem' }}>
            <button className="btn" disabled={!sel.size} onClick={cobrar}>Marcar {sel.size} como cobradas</button>
            <button className="btn sec mini" onClick={() => setMarcadas(new Set(pend.map((x) => x.id)))}>Todas</button>
            <button className="btn sec mini" onClick={() => setMarcadas(new Set())}>Ninguna</button>
          </div>
        </>
      )}
      {hecho && <p style={{ color: 'var(--bien)' }}>{hecho}</p>}
      {anuales.length > 0 && <>
        <h3 style={{ marginTop: '1rem' }}>Anuales que renuevan en {mesTxt(mes)}</h3>
        {anuales.map((x) => <div key={x.id} className="dia-fila">{x.mascota || x.cliente} <span className="tenue">{x.mascota ? x.cliente : ''} · {eur(x.cuota)}</span>
          <span style={{ marginLeft: 'auto' }}>{pagado(x, mes.slice(0, 4)) ? '✓ cobrada' : <button className="btn mini" onClick={() => updateDoc(doc(db, 'igualas', x.id), { [`pagos.${mes.slice(0, 4)}`]: { importe: x.cuota || 0, fecha: new Date().toISOString(), por: yo } })}>Cobrar</button>}</span></div>)}
      </>}
    </div>
  );
}

function Planes({ planes }) {
  const [l, setL] = useState(null);
  const lista = l ?? planes;
  const set = (i, k, v) => setL(lista.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const guardar = async () => { await setDoc(doc(db, 'config', 'igualas'), { planes: lista.filter((p) => p.nombre?.trim()).map((p) => ({ ...p, nombre: p.nombre.trim().slice(0, 80), cuota: validarImporte(p.cuota, { max: 5000 }).valor ?? 0 })) }, { merge: true }); setL(null); };
  return (
    <div className="tarjeta">
      <p className="tenue" style={{ fontSize: '.85rem', marginTop: 0 }}>Los planes que ofrecéis. Al elegirlos en una iguala se rellena solo la cuota.</p>
      <div className="ig-planes">
        {lista.map((p, i) => (
          <div key={i} className="ig-plan">
            <input className="campo" placeholder="Nombre (Plan adulto perro)" value={p.nombre || ''} onChange={(e) => set(i, 'nombre', e.target.value)} />
            <select value={p.clinica || ''} onChange={(e) => set(i, 'clinica', e.target.value)}><option value="">Todas</option>{CLINICAS.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select>
            <input className="campo num" placeholder="€" value={p.cuota ?? ''} onChange={(e) => set(i, 'cuota', e.target.value)} inputMode="decimal" style={{ width: 80 }} />
            <select value={p.periodicidad || 'mensual'} onChange={(e) => set(i, 'periodicidad', e.target.value)}><option value="mensual">/mes</option><option value="anual">/año</option></select>
            <input className="campo" placeholder="Qué incluye (vacunas, desparasitación, 2 consultas…)" value={p.incluye || ''} onChange={(e) => set(i, 'incluye', e.target.value)} style={{ flex: 2 }} />
            <button className="hosp-x" onClick={() => setL(lista.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
      </div>
      <div className="acciones" style={{ marginTop: '.6rem' }}>
        <button className="btn sec" onClick={() => setL([...lista, { nombre: '', clinica: '', cuota: '', periodicidad: 'mensual', incluye: '' }])}>+ Plan</button>
        {l && <button className="btn" onClick={guardar}>Guardar planes</button>}
      </div>
    </div>
  );
}

// Importar pegando desde Excel: Mascota | Dueño | Teléfono | Plan | Cuota | Alta | Cobrado hasta
const fechaDe = (t) => {
  const s = String(t || '').trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/); if (m) return `${m[3].length === 2 ? `20${m[3]}` : m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[/.-](\d{4})$/); if (m) return `${m[2]}-${m[1].padStart(2, '0')}-01`;
  return null;
};
// Importar el Excel de igualas (Clínica | Cliente | Mascota | Modalidad | Importe € | Plan | Alta | Válida hasta | Estado | Notas)
// La clínica se reconoce por su nombre (o el principio del nombre) en la columna «Clínica»
const CLIN_DE = (t) => clinicaPorTexto(t) || (CLINICAS.length === 1 ? CLINICAS[0].id : null);
const isoDe = (v) => (v instanceof Date ? new Date(v.getTime() - v.getTimezoneOffset() * 6e4).toISOString().slice(0, 10) : fechaDe(v));
export function filaExcelAIguala(r, hoy = hoyISO()) {
  const [cl, cliente, mascota, modalidad, importe, plan, alta, , estado, notas] = r;
  const clinica = CLIN_DE(cl);
  const a = isoDe(alta);
  if (!clinica || !a || !(cliente || mascota)) return null;
  const anual = /anual/i.test(modalidad || '');
  const est = String(estado || '').toLowerCase();
  const nota = String(notas || '').trim();
  const mes = hoy.slice(0, 7);
  const d = {
    clinica, cliente: String(cliente || '').replace(/\s+/g, ' ').trim(), mascota: String(mascota || '').trim(), plan: String(plan || '').trim(),
    cuota: Math.max(0, num(importe) || 0), periodicidad: anual ? 'anual' : 'mensual', metodo: anual ? '' : /domicil/i.test(modalidad || '') ? 'domiciliacion' : '',
    alta: a, baja: null, especie: /gato/i.test(plan || '') && !/perro/i.test(plan || '') ? 'gato' : /perro/i.test(plan || '') ? 'perro' : '', notas: nota, revisar: false,
  };
  if (anual) d.pagadoHasta = a.slice(0, 4); // pago único: el año de la compra está pagado
  else if (est.includes('no paga')) { const n = (nota.match(/devuelve ([a-z]{3})(?: y ([a-z]{3}))?/i) || []).filter(Boolean).length - 1; d.pagadoHasta = sumaMes(mes, -Math.max(1, n || 1)); }
  else if (est.includes('revis')) { const pago = nota.match(/pag[oó] en cl[ií]nica: (\d{2})-(\d{2})-(\d{4})/i); d.pagadoHasta = pago && `${pago[3]}-${pago[2]}` === mes ? mes : sumaMes(mes, -1); }
  else d.pagadoHasta = mes;
  if (est.includes('anul')) { d.baja = (nota.match(/(\d{2})-(\d{2})-(\d{4})/) || []).length ? nota.replace(/.*?(\d{2})-(\d{2})-(\d{4}).*/, '$3-$2-$1') : hoy; d.motivoBaja = nota || 'Anulada'; }
  if (est.includes('no paga') || est.includes('revis')) { d.revisar = true; d.notaRevision = nota || (est.includes('no paga') ? 'No paga' : 'Revisar'); }
  return d;
}
function ImportarExcel({ yo, existentes }) {
  const [filas, setFilas] = useState(null);
  const [hecho, setHecho] = useState('');
  const [err, setErr] = useState('');
  const leer = async (file) => {
    setErr(''); setHecho('');
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames.find((n) => /iguala/i.test(n)) || wb.SheetNames[0]];
      const t = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null });
      const i = t.findIndex((r) => /cl[ií]nica/i.test(String(r[0] || '')) && /cliente/i.test(String(r[1] || '')));
      if (i < 0) throw new Error('No encuentro la cabecera "Clínica | Cliente | Mascota | Modalidad…"');
      setFilas(t.slice(i + 1).map((r) => filaExcelAIguala(r)).filter(Boolean));
    } catch (e) { setErr(e.message); }
  };
  const dup = (d) => existentes.some((x) => x.clinica === d.clinica && (x.cliente || '').toLowerCase() === d.cliente.toLowerCase() && (x.mascota || '') === d.mascota && x.alta === d.alta && x.cuota === d.cuota);
  const nuevas = (filas || []).filter((d) => !dup(d));
  const importar = async () => {
    for (let i = 0; i < nuevas.length; i += 400) {
      const b = writeBatch(db);
      nuevas.slice(i, i + 400).forEach((d) => b.set(doc(collection(db, 'igualas')), { ...d, creado: new Date().toISOString(), creadoPor: yo, importado: true }));
      await b.commit();
    }
    setHecho(`${nuevas.length} igualas importadas.`); setFilas(null);
  };
  const cuenta = (f) => (filas || []).filter(f).length;
  return (
    <div className="tarjeta">
      <h3 style={{ marginTop: 0 }}>Importar el Excel de igualas</h3>
      <p className="tenue" style={{ fontSize: '.85rem' }}>Hoja con columnas Clínica · Cliente · Mascota · Modalidad · Importe € · Plan · Alta · Válida hasta · Estado · Notas. Las mensuales "Activa" se dan por cobradas hasta este mes; las "NO PAGA" salen con las cuotas devueltas como deuda y para revisar; las anuales, pagadas el año de la compra.</p>
      <input type="file" accept=".xlsx,.xls" onChange={(e) => e.target.files[0] && leer(e.target.files[0])} />
      {err && <p className="error">{err}</p>}
      {filas && <>
        <p style={{ margin: '.6rem 0' }}><b>{filas.length}</b> igualas leídas: {cuenta((d) => d.periodicidad === 'mensual')} mensuales, {cuenta((d) => d.periodicidad === 'anual')} anuales · {cuenta((d) => d.revisar)} para revisar · {cuenta((d) => d.baja)} anuladas{filas.length - nuevas.length ? ` · ${filas.length - nuevas.length} ya estaban` : ''}</p>
        <div className="ig-prev">{filas.slice(0, 200).map((d, i) => <div key={i} className={d.revisar ? 'mal' : dup(d) ? 'tenue' : ''}>{nombreClinica(d.clinica)} · <b>{d.cliente}</b>{d.mascota ? ` (${d.mascota})` : ''} · {d.plan} · {eur(d.cuota)}/{d.periodicidad === 'anual' ? 'año' : 'mes'} · desde {fecha(d.alta)} · cobrado hasta {d.pagadoHasta?.length === 7 ? mesTxt(d.pagadoHasta) : d.pagadoHasta}{d.revisar ? ' · ⚠️ revisar' : ''}{d.baja ? ' · anulada' : ''}</div>)}</div>
        <button className="btn" disabled={!nuevas.length} onClick={importar}>Importar {nuevas.length} igualas</button>
      </>}
      {hecho && <p style={{ color: 'var(--bien)' }}>{hecho}</p>}
    </div>
  );
}

function Importar(props) {
  return <><ImportarExcel {...props} /><ImportarPegar {...props} /></>;
}
function ImportarPegar({ planes, clinicas, yo, existentes }) {
  const [clinica, setClinica] = useState(clinicas[0]);
  const [txt, setTxt] = useState('');
  const [hecho, setHecho] = useState('');
  const filas = txt.split('\n').map((l) => l.split('\t').map((c) => c.trim())).filter((c) => c[0] && !/^mascota$/i.test(c[0]));
  const datos = filas.map((c) => {
    const plan = c[3] || '';
    const p = planes.find((x) => x.nombre.toLowerCase() === plan.toLowerCase());
    const hasta = fechaDe(c[6]);
    return { clinica, mascota: c[0], cliente: c[1] || '', telefono: c[2] || '', plan, cuota: Math.max(0, num(String(c[4] || '').replace(/[€\s]/g, '')) ?? p?.cuota ?? 0), periodicidad: p?.periodicidad || (/an/i.test(c[7] || '') ? 'anual' : 'mensual'), metodo: 'domiciliacion', alta: fechaDe(c[5]), pagadoHasta: hasta ? hasta.slice(0, 7) : null, especie: 'perro', notas: '' };
  });
  const malas = datos.filter((d) => !d.alta);
  const dup = (d) => existentes.some((x) => x.clinica === d.clinica && (x.mascota || '').toLowerCase() === (d.mascota || '').toLowerCase() && (x.cliente || '').toLowerCase() === (d.cliente || '').toLowerCase() && (x.alta || '') === (d.alta || ''));
  const importar = async () => {
    const ok = datos.filter((d) => d.alta && !dup(d));
    for (const d of ok) await addDoc(collection(db, 'igualas'), { ...d, creado: new Date().toISOString(), creadoPor: yo, importado: true });
    setHecho(`${ok.length} igualas importadas.`); setTxt('');
  };
  return (
    <div className="tarjeta">
      <h3 style={{ marginTop: 0 }}>O pegar filas sueltas</h3>
      <p>Copia las filas de tu Excel y pégalas aquí. Columnas en este orden:</p>
      <p className="ig-cols"><span>Mascota</span><span>Dueño</span><span>Teléfono</span><span>Plan</span><span>Cuota €</span><span>Alta (dd/mm/aaaa)</span><span>Cobrado hasta (mm/aaaa)</span></p>
      <p className="tenue" style={{ fontSize: '.8rem' }}>"Cobrado hasta": el último mes que ya está pagado; lo de antes se da por cobrado. Si alguno no paga, déjalo vacío o pon el último mes que pagó y saldrá en impagos.</p>
      {clinicas.length > 1 && <label className="tenue">Clínica <select value={clinica} onChange={(e) => setClinica(e.target.value)}>{CLINICAS.filter((c) => clinicas.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}</select></label>}
      <textarea className="campo" rows={8} value={txt} onChange={(e) => { setTxt(e.target.value); setHecho(''); }} placeholder={'Toby\tMarta López\t600111222\tPlan adulto\t19,90\t15/03/2025\t09/2026'} style={{ width: '100%', fontFamily: 'monospace', fontSize: '.8rem', marginTop: '.5rem' }} />
      {datos.length > 0 && (
        <>
          <div className="ig-prev">
            {datos.slice(0, 50).map((d, i) => (
              <div key={i} className={!d.alta ? 'mal' : dup(d) ? 'tenue' : ''}>
                <b>{d.mascota}</b> · {d.cliente} · {d.plan} · {eur(d.cuota)} · desde {d.alta ? fecha(d.alta) : '¿fecha?'}{d.pagadoHasta ? ` · cobrado hasta ${mesTxt(d.pagadoHasta)}` : ' · sin cobros'}{dup(d) ? ' · ya existe' : ''}
              </div>
            ))}
          </div>
          {malas.length > 0 && <p className="error" style={{ fontSize: '.85rem' }}>{malas.length} fila(s) sin fecha de alta válida: no se importan.</p>}
          <button className="btn" onClick={importar}>Importar {datos.filter((d) => d.alta && !dup(d)).length} igualas en {nombreClinica(clinica)}</button>
        </>
      )}
      {hecho && <p style={{ color: 'var(--bien)' }}>{hecho}</p>}
    </div>
  );
}

// Para el contador del menú: igualas con impago o para revisar
export function useImpagosIgualas(clinicas, activo = true) {
  const l = useColeccion('igualas', clinicas, [], activo);
  return (l || []).filter((ig) => estadoIguala(ig).impago).length;
}
